/**
 * **Wo eine offene Aufgabe im Trichter steht – und wann sie nirgends steht**
 * (19.08.2026, [E-131](../../../docs/ENTSCHEIDUNGEN.md)).
 *
 * Nils, mit einem Bild der Pyramide: *„fehlen da nicht noch die nicht zugewiesenen oder sind
 * das dann die aus dem Eingang?"* Die Nicht-Zugewiesenen **sind** die Pool-Ebenen – „im Pool"
 * heißt genau das. Der Eingang stand nicht im Bild. Beim Nachsehen kam ein Loch heraus, das
 * niemand gesucht hatte.
 *
 * ## Das Leck
 *
 * Der Eingang ist **nicht über den Status definiert**, sondern über drei Verneinungen
 * (`eingang.liste`): offen · in keinem Pool · kein Zeitfenster · kein Wunsch-Entwickler.
 * Daneben stand die Annahme, auf der alles ruhte:
 *
 * > *„Sobald eines der drei gesetzt ist, hat jemand die Aufgabe angefasst; wo sie dann
 * > weitergeht, zeigen Pools und Trichter."*
 *
 * Der Trichter kennt aber nur **Pools und Bearbeiter**. Wer in der Planung ein Zeitfenster
 * setzt, ohne einen Pool zu setzen, nimmt die Aufgabe damit aus dem Eingang **und** stellt
 * sie in keine Ebene: eingeplant, und von niemandem zu ziehen. Sichtbar nur noch in „Alle
 * Aufgaben". Genau dafür gibt es {@link inKeinerEbene} – die Zahl ist die Zusage, dass der
 * Trichter nichts still verliert.
 *
 * ## Warum die Regel hier steht und nicht in der Ansicht
 *
 * Die Eingangs-Ansicht zieht ihre Grenze in SQL, der Trichter zieht sie im Client. **Zwei
 * Auslegungen derselben Frage** sind der Grund, warum das Loch überhaupt entstehen konnte.
 * Also steht sie einmal hier – dieselbe Überlegung wie bei `poolSortierung` daneben.
 *
 * ## Eine Abweichung, die bleibt
 *
 * `eingang.liste` lässt alles herein, was `NOT IN ('erledigt', 'zurueckgestellt')` ist – also
 * auch `in_arbeit` und `review`. Für den Trichter ist das **kein** Leck: eine Aufgabe in
 * Arbeit hat ihre eigene Ebene. Deshalb fragt {@link ohneVorrat} nur nach `bereit` und
 * `eingang`. Ob eine Aufgabe in Arbeit in der Eingangs-Liste stehen sollte, ist eine andere
 * Frage – sie steht in [OFFENE-FRAGEN.md](../../../docs/OFFENE-FRAGEN.md).
 */

/**
 * Was zur Einordnung gebraucht wird. Absichtlich der kleinste Schnitt: `inPools` rechnet die
 * Ansicht aus den Pools, die sie ohnehin geladen hat, `hatWunsch` kommt aus `tasks.all`.
 */
export type TrichterAufgabe = {
  status: string
  assigneeId: string | null
  zeitfenster: string | null
  /** Steht jemand unter „kommen in Frage"? Ohne diese Angabe wäre der Eingang zu groß gezählt. */
  hatWunsch: boolean
  /** In wie vielen Pools die Aufgabe liegt – `0` heißt: in keinem Vorrat. */
  inPools: number
}

/**
 * **Offen, in keinem Vorrat, niemandem zugeteilt.** Der unsortierte Rest.
 *
 * `zurueckgestellt` fehlt bewusst: das ist geparkt und kein Versehen. `erledigt` hat seine
 * eigene Zeile, `in_arbeit` und `review` haben ihre eigenen Ebenen.
 */
export function ohneVorrat(a: TrichterAufgabe): boolean {
  if (a.assigneeId !== null) return false
  if (a.inPools > 0) return false
  return a.status === 'bereit' || a.status === 'eingang'
}

/**
 * **Wartet auf Vorqualifizierung** – steht in der Eingangs-Ansicht.
 *
 * Niemand hat die Aufgabe angefasst: kein Pool, kein Zeitfenster, kein Wunsch-Entwickler.
 */
export function wartetAufVorqualifizierung(a: TrichterAufgabe): boolean {
  return ohneVorrat(a) && a.zeitfenster === null && !a.hatWunsch
}

/**
 * **Angefasst, aber in keiner Ebene** – das Leck aus dem Kopfkommentar.
 *
 * Jemand hat ein Zeitfenster gesetzt oder einen Namen eingetragen. Damit ist die Aufgabe aus
 * dem Eingang heraus – aber ohne Pool und ohne Bearbeiter steht sie in keinem Vorrat, aus dem
 * jemand ziehen könnte.
 */
export function inKeinerEbene(a: TrichterAufgabe): boolean {
  return ohneVorrat(a) && !wartetAufVorqualifizierung(a)
}

/**
 * **Liegt im Vorrat, aber niemand ist dafür vorgesehen**
 * ([Z-05](../../../docs/phasen/zusatz-02-mein-tag.md#z-05--der-trichter-bekommt-ohne-wunsch-entwickler)).
 *
 * Der Auslöser ist eine Messung vom 24.08.2026: von **76 ziehbaren Aufgaben hatten 55 keinen
 * Wunsch-Entwickler** – weder an der Aufgabe noch am Vorhaben. Nils' Ziel: *„dass sich dadurch
 * ein schönes Gefüge erstellt, wo die Entwickler nur die Aufgaben/Pools sehen, wo ich oder der
 * Leiter die für vorgesehen haben."*
 *
 * ## Warum diese Menge und keine andere
 *
 * | zählt mit | zählt nicht |
 * |---|---|
 * | liegt in mindestens einem Pool | im Eingang – dort ist die Lücke der Normalzustand |
 * | offen (`bereit`, `eingang`) | erledigt, geparkt, in Arbeit, zur Abnahme |
 * | niemand hat sie genommen | hat einen Bearbeiter – die Frage ist beantwortet |
 *
 * Eine Aufgabe **im Eingang** ohne Wunsch ist kein Mangel, sondern der Zustand vor der Triage –
 * sie steht bereits in der Zulauf-Zeile. Hier geht es um die andere Hälfte: Aufgaben, die schon
 * im Vorrat liegen und aus denen trotzdem niemand gezielt zieht.
 *
 * ## Warum der Wunsch am **Vorhaben** mitzählt
 *
 * Weil er wirkt ([E-108](../../../docs/ENTSCHEIDUNGEN.md)): eine Aufgabe ohne eigenen Wunsch,
 * deren Vorhaben zwei Namen trägt, ist vorgesehen. Sie hier aufzuführen hieße, jemanden zu
 * bitten, eine Angabe zu wiederholen, die schon dasteht.
 *
 * ## Warum ein Bearbeiter aus der **Quelle** nicht mitzählt
 *
 * Ein GitLab-Assignee ist kein Vorsehen ([E-155](../../../docs/ENTSCHEIDUNGEN.md) beschreibt,
 * wo er mitzählt – nämlich in der Anzeige „kommen in Frage"). Die Frage hier ist enger: *hat
 * jemand bei uns entschieden, für wen das gedacht ist?* Ein importiertes Feld ist keine
 * Entscheidung – dieselbe Grenze, die auch der Eingang zieht.
 */
export function ohneWunschImVorrat(a: {
  status: string
  assigneeId: string | null
  inPools: number
  /** Wunsch **an der Aufgabe** – aus `task_wunsch`, nicht die wirksame Menge. */
  hatWunsch: boolean
  /** Trägt das Vorhaben einen Wunsch? Ohne Vorhaben: `false`. */
  vorhabenHatWunsch: boolean
}): boolean {
  if (a.inPools === 0) return false
  if (a.assigneeId !== null) return false
  if (a.status !== 'bereit' && a.status !== 'eingang') return false
  return !a.hatWunsch && !a.vorhabenHatWunsch
}
